import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from "@chakra-ui/react";
import { ColumnDef, RowData } from "@tanstack/react-table";
import { useTableColumnVisibility } from "store/columnVisibility.hooks";
import { getColumnId, isColumnHideable } from "../../columnVisibility.utils";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    // Menu label for columns whose header is not a plain string.
    toggleLabel?: string;
  }
}

type ToggleEntry = { id: string; label: string };

const getToggleEntries = <T,>(columns: ColumnDef<T>[]): ToggleEntry[] =>
  columns.flatMap((column) => {
    const id = getColumnId(column);

    if (id === undefined || !isColumnHideable(column)) {
      return [];
    }

    const label =
      typeof column.header === "string" && column.header !== ""
        ? column.header
        : (column.meta?.toggleLabel ?? id);

    return [{ id, label }];
  });

type TableColumnsToggleProps<T> = {
  tableId: string;
  columns: ColumnDef<T>[];
};

const TableColumnsToggle = <T,>({
  tableId,
  columns,
}: TableColumnsToggleProps<T>) => {
  const { isHidden, toggleColumn, resetColumns } =
    useTableColumnVisibility(tableId);

  const entries = getToggleEntries(columns);
  const visibleIds = entries
    .filter((entry) => !isHidden(entry.id))
    .map((entry) => entry.id);

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        size="sm"
        variant="ghost"
        leftIcon={<SettingsIcon />}
        rightIcon={<ChevronDownIcon />}
      >
        Columns
      </MenuButton>
      <MenuList maxH="320px" overflowY="auto">
        <MenuOptionGroup type="checkbox" value={visibleIds}>
          {entries.map((entry) => (
            <MenuItemOption
              key={entry.id}
              value={entry.id}
              onClick={() => toggleColumn(entry.id)}
            >
              {entry.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
        <MenuDivider />
        <MenuItem onClick={() => resetColumns()}>Reset to default</MenuItem>
      </MenuList>
    </Menu>
  );
};

export { TableColumnsToggle };
